/**
 * Summary
 * This displays a popup that contains information about a currently
 * selected event
 */

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useApp, useModal, useTime } from "../_contexts";
import { Button, Icon } from "../_components";
import {
  ChronosDate,
  getArticleIcon,
  getDefaultCoverImageURL,
  parseBBCode,
} from "../_helpers";
import ReactTooltip from "react-tooltip";

const DEFAULT_DATE_MASK = `$DD$ $MMMM$ $YYYY$`;
export function Popup() {
  const [coverImage, setCoverImage] = useState(undefined);
  const { items: images } = useSelector((state) => state.images);
  const { data: chronicle } = useSelector((state) => state.chronicle);
  const { details: world } = useSelector((state) => state.world);
  const { items: events } = useSelector((state) => state.event);
  const { items: eventTypes } = useSelector((state) => state.eventTypes);

  let headerBG = "";
  let title = "";
  let excerpt = "";
  let icon = null;
  let button = false;

  const { closePopup, popupProps, eventPopupOpen, openSidebar, editMode } =
    useApp();
  const { calendar } = useTime();
  const { openModal } = useModal();
  let dateDisplay = "";
  if (popupProps) {
    // console.log('Popup Props', popupProps);
    const { event } = popupProps;
    const { fromDate, toDate } = event ? event : {};
    const isRange = toDate && toDate !== null;
    title = event.title;
    if (event.excerpt && event.excerpt.length > 0) {
      excerpt = event.excerpt;
    } else {
      excerpt = event.description;
    }

    if (event && event.category && eventTypes) {
      icon = eventTypes.find((t) => t._id === event.category)?.icon || null;
      // console.log("---===--- icon", icon);
    }

    headerBG = event?.cover?.url || getDefaultCoverImageURL();
    // console.log('POPUP BG', headerBG);
    button = true;

    //Display Date
    if (fromDate && fromDate !== null) {
      let start = "";
      let end = "";
      let startDate = new ChronosDate(fromDate, { calendar });
      let posSuffix = "";
      let negSuffix = "";
      if (world && world.worldTime) {
        let postAbbr = world.worldTime.afterZeroAbbr;
        let preAbbr = world.worldTime.beforeZeroAbbr;
        if (postAbbr && postAbbr.length > 0) {
          posSuffix = postAbbr;
        }
        if (preAbbr && preAbbr.length > 0) {
          negSuffix = preAbbr;
        }
      }

      start = startDate.format(
        calendar.dateMask || DEFAULT_DATE_MASK,
        undefined,
        posSuffix,
        negSuffix
      );

      if (toDate && toDate !== null) {
        let endDate = new ChronosDate(toDate, { calendar });
        end = endDate.format(
          calendar.dateMask || DEFAULT_DATE_MASK,
          undefined,
          posSuffix,
          negSuffix
        );
      }

      dateDisplay = start;
      if (isRange) {
        dateDisplay += " — " + end;
      }
    }
  }

  const handleReadMoreClick = (ev) => {
    ev.preventDefault();
    const { event, type } = popupProps;
    const displayType = type && type.length > 0 ? type : "event";
    const eventFromStore = events.find((e) => e._id === event._id);
    openSidebar({
      type: displayType,
      payload: { event: eventFromStore },
    });
    closePopup();
  };

  const handleEditClick = (ev) => {
    ev.preventDefault();
    const { event } = popupProps;
    const eventFromStore = events.find((e) => e._id === event._id);
    openModal("newEvent", { eventItem: eventFromStore });
    closePopup();
  };

  let classes = "chronos-event-popup";

  if (eventPopupOpen) {
    classes += " chronos-popup-open";
  }

  excerpt =
    '<div class="chronos-popup-excerpt">' +
    parseBBCode(excerpt) +
    '</div><div class="chronos-read-more">Read the full story</div>';

  useEffect(() => {
    if (chronicle && popupProps && popupProps !== null) {
      const { event } = popupProps;
      // console.log('Popup Setting', event);
      if (event && event.cover) {
        const { cover } = event;
        if (cover === null) {
          const { defaultCover } = chronicle;
          if (defaultCover && defaultCover !== null) {
            if (typeof defaultCover === "string") {
              // console.log('Looking up Chronicle Default Cover');
            } else if (defaultCover._id) {
              setCoverImage(`url(${defaultCover.url})`);
            }
          } else {
            // setCoverImage(
            //   'url(https://www.worldanvil.com/images/logo-550px.png)'
            // );
            setCoverImage(`url(${getDefaultCoverImageURL()})`);
          }
        } else {
          if (typeof cover === "object" && cover._id) {
            setCoverImage(`url(${cover.url})`);
          } else {
            // console.log('Looking up Event Cover');
            const image = images.find((i) => i._id === cover);
            if (image) {
              setCoverImage(`url(${image.url})`);
            } else {
              // console.log('No Event Cover found!');
              const { defaultCover } = chronicle;
              if (defaultCover && defaultCover !== null) {
                if (typeof defaultCover === "string") {
                  // console.log('Looking up Chronicle Default Cover');
                  const image = images.find((i) => i._id === defaultCover);
                  if (image) {
                    setCoverImage(`url(${image.url})`);
                  }
                } else if (defaultCover._id) {
                  setCoverImage(`url(${defaultCover.url})`);
                }
              } else {
                setCoverImage(`url(${getDefaultCoverImageURL()})`);
              }
            }
          }
        }
      } else {
        //No Cover Set for the Event
        const { defaultCover } = chronicle;
        if (defaultCover && defaultCover !== null) {
          if (typeof defaultCover === "string") {
            // console.log('Looking up Chronicle Default Cover', defaultCover);
            const image = images.find((i) => i._id === defaultCover);
            if (image) {
              setCoverImage(`url(${image.url})`);
            }
          } else if (defaultCover._id) {
            setCoverImage(`url(${defaultCover.url})`);
          }
        } else {
          setCoverImage(`url(${getDefaultCoverImageURL()})`);
        }
      }
    }
  }, [chronicle, popupProps]);

  useEffect(() => {
    ReactTooltip.rebuild();
    const handleKeyTrigger = (ev) => {
      // console.log('KEY PRESSED', ev.key);
      if (ev.key === "Escape") {
        closePopup();
      }
    };
    if (eventPopupOpen) {
      window.addEventListener("keyup", handleKeyTrigger);
    }

    return () => window.removeEventListener("keyup", handleKeyTrigger);
  });

  const isEditor = chronicle && chronicle.isEditor;
  const eventPopupTooltip = "chronosEventPopupTooltip";
  return (
    <div className={classes}>
      <div
        className="chronos-popup-header"
        style={{
          // backgroundSizing: 'cover',
          backgroundImage: coverImage,
          backgroundPosition: "50% 50%",
        }}
      >
        {icon && (
          <div className="chronos-popup-icon">
            <Icon icon={icon} />
          </div>
        )}
        <button className="chronos-popup-close" onClick={closePopup}>
          <Icon icon="fas fa-times" />
        </button>
        {editMode && isEditor && (
          <Button
            className="chronos-popup-edit-event-btn"
            color="primary"
            onClick={handleEditClick}
            size="xs"
          >
            <Icon
              icon="fas fa-pencil fa-fw"
              data-tip="Edit Event"
              data-for={eventPopupTooltip}
            />
          </Button>
        )}
      </div>
      <div className="chronos-popup-body" onClick={handleReadMoreClick}>
        <h4 className="chronos-popup-title">{title}</h4>
        {dateDisplay && dateDisplay.length > 0 && (
          <h6 className="chronos-popup-date">{dateDisplay}</h6>
        )}
        <hr />
        <div
          className="chronos-popup-description"
          dangerouslySetInnerHTML={{ __html: excerpt }}
        />
        {/* <div className="chronos-popup-options">
          {isEditor && editMode && (
            <div className="chronos-popup-edit">
              <Button color="secondary" size="xs" onClick={handleEditClick}>
                <Icon icon="far fa-edit fa-fw" />
                &nbsp;Edit Event
              </Button>
            </div>
          )}
          {button && (
            <Button color="primary" size="xs">
              <Icon icon="fas fa-eye" />
              &nbsp; Read More
            </Button>
          )}
        </div> */}
      </div>
      <ReactTooltip effect="solid" id={eventPopupTooltip} />
    </div>
  );
}
